import {
  SidebarList,
  SidebarListItemProps,
} from "@/lib/Components/SideBarList/SideBarList";
import { useState } from "react";
import { useInfiniteGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  SupplierOfficeListItem,
  supplierOfficeListQuery,
} from "@/app/Offices/GraphQL/supplierOfficeListQuery";
import { keepPreviousData } from "@tanstack/react-query";
import { getOfficeListQueryVariables } from "@/app/Offices/Utils/getOfficeListQueryVariables";
import { useSearch } from "@tanstack/react-router";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";

export function OfficeListScreen() {
  const searchParams = useSearch({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/offices",
  });
  const [search, setSearch] = useState(searchParams.officeSearch ?? "");
  const { supplierId } = useGlobalSupplier();

  const { data, fetchNextPage, isFetchingNextPage, isFetching } =
    useInfiniteGqlQuery(
      supplierOfficeListQuery,
      getOfficeListQueryVariables(
        { ...searchParams, officeSearch: search },
        supplierId,
      ),
      {
        placeholderData: keepPreviousData,
        initialPageParam: {
          page: 1,
        },
        getNextPageParam: (lastPage) => {
          return lastPage.supplierOffices.paginatorInfo.hasMorePages
            ? {
                page: lastPage.supplierOffices.paginatorInfo.currentPage + 1,
              }
            : undefined;
        },
      },
    );

  const items = data?.pages.flatMap((page) => page.supplierOffices.data) ?? [];
  const totalCount = data?.pages[0].supplierOffices.paginatorInfo.count ?? 0;

  return (
    <SidebarList<SupplierOfficeListItem>
      items={items}
      totalCount={totalCount}
      linkProps={(item) => ({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/offices/$id",
        params: (prev) => ({
          ...prev,
          id: item.id,
        }),
      })}
      search={search}
      onSearchChange={(newSearch) => {
        setSearch(newSearch);
      }}
      onClearFilters={() => {
        setSearch("");
      }}
      listRoute="/$tenant/$orgId/supplier/{-$supplierId}/offices"
      createRoute="/$tenant/$orgId/supplier/{-$supplierId}/offices/create"
      isFetchingNextPage={isFetchingNextPage}
      isFetching={isFetching}
      fetchNextPage={fetchNextPage}
      itemNode={(item) => <OfficeItem model={item} />}
    />
  );
}

function OfficeItem({ model }: SidebarListItemProps<SupplierOfficeListItem>) {
  const { hasSupplier } = useGlobalSupplier();

  return (
    <div className="relative">
      <p className="truncate text-sm text-gray-900">{model.name}</p>
      <p className="truncate text-xs text-gray-500">
        {model.address.formatted_address_line_1}
      </p>
      <p className="truncate text-xs text-gray-500">
        {model.address.formatted_address_line_2}
      </p>

      {!hasSupplier ? (
        <p className="mt-3 truncate border-l-2 border-blue-400 pl-2 text-xs text-gray-500">
          {model.supplier.name}
        </p>
      ) : null}
    </div>
  );
}
